import React, { useState, useEffect, useCallback } from "react";
import { Modal, Flowbite } from "flowbite-react";
import CommonHeader from "../components/CommonHeader";
import ToastNotification, { showToast } from "../components/Toaster";
import usersApi from "../services/users/UserApi";
import PillButton from "../components/button/Pills";

interface VoiceBot {
  id: number;
  botId: string;
}

const VoiceBotPage: React.FC = () => {
  const [voiceBots, setVoiceBots] = useState<VoiceBot[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedBot, setSelectedBot] = useState<VoiceBot | null>(null);
  const [botForm, setBotForm] = useState({ id: 0, botId: "" });
  const [formErrors, setFormErrors] = useState<{ botId?: string }>({});
  const [loading, setLoading] = useState(false);

  const fetchVoiceBots = useCallback(async () => {
    setLoading(true);
    try {
      const response = await usersApi.getVoiceBots();
      if (response.success) {
        const bots: VoiceBot[] = response.value.data.contents.map((bot: { id: number; botId: string }) => ({
          id: bot.id,
          botId: bot.botId,
        }));
        setVoiceBots(bots);
      } else {
        showToast("Failed to fetch voice bots.", "error");
      }
    } catch (error) {
      showToast("An error occurred while fetching voice bots.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVoiceBots();
  }, [fetchVoiceBots]);

  const validateForm = (): boolean => {
    const errors: { botId?: string } = {};

    if (!botForm.botId.trim()) {
      errors.botId = "Bot ID is required.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDeleteClick = (bot: VoiceBot): void => {
    setSelectedBot(bot);
    setOpenDeleteModal(true);
  };

  const handleDelete = async () => {
    if (selectedBot) {
      try {
        const response = await usersApi.deleteVoiceBot(selectedBot.id);
        if (response.success) {
          setVoiceBots(voiceBots.filter((bot) => bot.id !== selectedBot.id));
          showToast("Voice bot deleted successfully!", "success");
        } else {
          showToast("Failed to delete voice bot.", "error");
        }
      } catch (error) {
        showToast("An error occurred while deleting the voice bot.", "error");
      }
      setOpenDeleteModal(false);
    }
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setBotForm({ id: 0, botId: "" });
    setOpenAddEditModal(true);
  };

  const handleEditClick = (bot: VoiceBot): void => {
    setIsEditing(true);
    setBotForm(bot);
    setOpenAddEditModal(true);
  };

  const handleSaveBot = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (isEditing) {
        const payload = {
          id: botForm.id,
          botId: botForm.botId,
        };
        const response = await usersApi.updateVoiceBot(payload);
        if (response.success) {
          showToast("Voice bot updated successfully!", "success");
        } else {
          showToast("Failed to update voice bot.", "error");
        }
      } else {
        const payload = {
          botId: botForm.botId,
        };
        const response = await usersApi.setVoiceBot(payload);
        if (response.success) {
          showToast("Voice bot added successfully!", "success");
        } else {
          showToast("Failed to add voice bot.", "error");
        }
      }
    } catch (error) {
      showToast("An error occurred while saving the voice bot.", "error");
    } finally {
      fetchVoiceBots();
    }

    setOpenAddEditModal(false);
  };

  return (
    <Flowbite>
      <div className="flex w-full h-[calc(100vh-67px)]">
        <ToastNotification />
        <div className="flex-1">
          <CommonHeader title="Voice Bots" buttonText="Add Voice Bot" onButtonClick={handleAddClick} />

          {loading ? (
            <p className="text-ez-text-muted text-center mt-[200px]">Loading...</p>
          ) : voiceBots.length === 0 ? (
            <p className="text-ez-text-muted text-center mt-[200px]">No records found.</p>
          ) : (
            <div className="w-full p-16">
              <div className="relative overflow-x-auto shadow-md border border-ez-border bg-ez-surface">
                <table className="w-full text-sm text-left rtl:text-right text-ez-text-secondary dark:text-ez-text-primary">
                  <thead className="text-xs uppercase bg-ez-elevated dark:bg-ez-elevated dark:text-ez-text-secondary text-ez-text-secondary">
                    <tr>
                      <th scope="col" className="px-6 py-3">ID</th>
                      <th scope="col" className="px-6 py-3">Bot Id</th>
                      <th scope="col" className="px-6 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {voiceBots.map((bot) => (
                      <tr key={bot.id} className="border-b border-ez-border odd:bg-ez-surface even:bg-ez-elevated hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors">
                        <td className="px-6 py-4 text-ez-text-primary">{bot.id}</td>
                        <td className="px-6 py-4 text-ez-text-primary">{bot.botId}</td>
                        <td className="px-6 py-4">
                          <button
                            type="button"
                            title="Edit voice bot"
                            className="action-btn"
                            onClick={() => handleEditClick(bot)}
                          >
                            <svg
                              stroke="currentColor"
                              fill="none"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              height="15px"
                              width="15px"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          <button
                            type="button"
                            title="Delete voice bot"
                            className="action-btn ml-2"
                            onClick={() => handleDeleteClick(bot)}
                          >
                            <svg
                              stroke="currentColor"
                              fill="currentColor"
                              strokeWidth="0"
                              viewBox="0 0 448 512"
                              height="1em"
                              width="1em"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <Modal show={openDeleteModal} size="md" onClose={() => setOpenDeleteModal(false)} popup>
          <Modal.Header className="bg-ez-elevated rounded-t-lg ">Delete Voice Bot</Modal.Header>
          <Modal.Body className="bg-ez-surface">
            <div className="text-center">
              <p className="text-ez-text-primary">Are you sure you want to delete this voice bot?</p>
              <div className="flex justify-center gap-3 mt-4">
                <PillButton variant="danger" onClick={handleDelete}>Yes</PillButton>
                <PillButton variant="secondary" onClick={() => setOpenDeleteModal(false)}>Cancel</PillButton>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal show={openAddEditModal} size="md" onClose={() => setOpenAddEditModal(false)} popup>
          <Modal.Header className="bg-ez-elevated rounded-t-lg">{isEditing ? "Edit Voice Bot" : "Add Voice Bot"}</Modal.Header>
          <Modal.Body className="bg-ez-surface">
            <div className="space-y-4">
              <div>
                <label className="block text-ez-text-secondary mb-1">
                  Bot ID
                </label>
                <input
                  type="text"
                  placeholder="Bot ID"
                  value={botForm.botId}
                  onChange={(e) => setBotForm({ ...botForm, botId: e.target.value })}
                  className="w-full p-2 border border-ez-border rounded bg-ez-elevated text-ez-text-primary placeholder:text-ez-text-muted"
                />
                {formErrors.botId && <p className="text-red-500 text-sm">{formErrors.botId}</p>}
              </div>
              <div className="flex justify-end gap-3">
                <PillButton variant="dark" onClick={handleSaveBot}>
                  {isEditing ? "Update" : "Save"}
                </PillButton>
                <PillButton variant="secondary" onClick={() => setOpenAddEditModal(false)}>
                  Cancel
                </PillButton>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </Flowbite>
  );
};

export default VoiceBotPage;




